require("./threepl-services-table-row.scss");

import {ChangeDetectionStrategy, Component, computed, inject, input, output} from "@angular/core";
import {NgxPermissionsService} from "ngx-permissions";
import * as _ from "lodash";
import {bzIconInfoOutline} from "@bringoz/ui/icons";
import {ServiceTaskContact, ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import {ThreePLServicesTableDynamicColumn} from "../threepl-services-table/threepl-services-table.component";
import {ServiceActionEvent} from "../threepl-service-actions/threepl-service-actions.component";
import {FulfillmentSubTypeType} from "../../../../fulfillment/types/fulfillment-sub-type.type";
import {BringozPhonePipe} from "../../../../commons/angular/pipes/bringoz-phone.pipe";
import {CommonsDialogService} from "../../../../commons/services/commons-dialog.service";
import {SplitService} from "../../../../commons/services/split-service.service";

interface ContactDisplay {
  parts: string[];
  label: string;
  tooltip: string;
}

@Component({
  selector: "app-threepl-services-table-row",
  templateUrl: "./threepl-services-table-row.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "class": "tr",
    "[class.selected]": "isSelected()",
    "(click)": "onRowClick($event)",
  },
})
export class ThreeplServicesTableRowComponent {
  readonly service = input.required<ThreePLServiceDetails>();
  readonly selectedIds = input<ReadonlySet<string>>(new Set());
  readonly columns = input<Record<string, boolean>>({});
  readonly dynamicColumns = input<ThreePLServicesTableDynamicColumn[]>([]);

  readonly toggle = output<ThreePLServiceDetails>();
  readonly addNote = output<ServiceActionEvent>();
  readonly moveToInternal = output<ServiceActionEvent>();
  readonly cancel = output<ServiceActionEvent>();

  readonly infoIcon = bzIconInfoOutline;

  private readonly permissions = inject(NgxPermissionsService);
  private readonly commonsDialogService = inject<CommonsDialogService>("commonsDialogService" as never);
  private readonly sitePrograms = inject<SplitService>("splitService" as never).getSiteProgramTypesMap();
  private readonly phonePipe = new BringozPhonePipe(inject("splitService" as never));

  readonly isSelected = computed(() => this.selectedIds().has(this.service().id));

  readonly canUpdate = !!this.permissions.getPermission("fulfillment.step.update") || !!this.permissions.getPermission("fulfillment.service.update");

  readonly derived = computed(() => {
    const steps = this.service().fulfillmentStepInfoList ?? [];
    return {
      omsOrderIds: _.uniq(steps.map((s) => s.omsOrderId).filter((id): id is string => !!id)),
      programNames: _.uniq(steps.filter((s) => !!s.programType).map((s) => this.sitePrograms[s.programType])),
      branchReferences: _.uniqBy(steps.flatMap((s) => s.branchReferences ?? []), "id"),
    };
  });

  readonly areaNames = computed(() => ({
    pickup: Object.values(this.service().pickup?.address?.areaIdToNameMap ?? {}),
    destination: Object.values(this.service().destination?.address?.areaIdToNameMap ?? {}),
  }));

  readonly pickupContact = computed(() => this.contactDisplay(this.service().pickup?.contact));
  readonly destinationContact = computed(() => this.contactDisplay(this.service().destination?.contact));

  onRowClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.canUpdate) {
      this.toggle.emit(this.service());
    }
  }

  onCheckboxClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggle.emit(this.service());
  }

  onOpenAttemptInfoDialog(event: MouseEvent): void {
    event.stopPropagation();
    this.commonsDialogService.attemptInfoDialog(this.service(), FulfillmentSubTypeType.ThirdParty);
  }

  private contactDisplay(contact: ServiceTaskContact | undefined): ContactDisplay | undefined {
    if (!contact) {
      return undefined;
    }
    const parts = [contact.businessName, contact.name].filter((part) => !!part);
    const label = parts.join(" / ");
    const phones = [contact.phoneNo, contact.additionalPhoneNo].filter((phone) => !!phone).map((phone) => String(this.phonePipe.transform(phone)));
    return {parts, label, tooltip: [label, ...phones].filter((part) => !!part).join(", ")};
  }
}
